import React, { useState } from 'react';
import OrderItems from '../components/OrderItems';
import DesignArtwork from '../components/DesignArtwork';
import { PlusIcon, XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { productColors } from '../config/colors';

export default function OrderDetails({ formData, setFormData, currentItem, setCurrentItem }) {
  const [error, setError] = useState('');
  const [snapshots, setSnapshots] = useState({
    front: null,
    back: null
  });

  const handleNotesChange = (side, value) => {
    setFormData(prev => ({
      ...prev,
      notes: {
        ...prev.notes,
        [side]: value
      }
    }));
  };

  const handleArtworkChange = (side, files) => {
    // Revoke any existing object URLs to prevent memory leaks
    formData.artwork[side]?.forEach(artworkItem => {
      if (artworkItem.preview) {
        URL.revokeObjectURL(artworkItem.preview);
      }
    });

    // Reset snapshot when artwork changes
    setSnapshots(prev => ({
      ...prev,
      [side]: null
    }));

    setFormData(prev => ({
      ...prev,
      artwork: {
        ...prev.artwork,
        [side]: files.map(file => ({
          file,
          preview: URL.createObjectURL(file)
        }))
      }
    }));
  };

  const handleSaveSnapshot = (side, snapshot) => {
    console.log(`Saving ${side} snapshot`);
    setSnapshots(prev => ({
      ...prev,
      [side]: snapshot
    }));
  };

  const validateItem = () => {
    setError('');

    if (!currentItem.quantity || currentItem.quantity <= 0) {
      setError('Please specify the quantity');
      return false;
    }
    if (!currentItem.color) {
      setError('Please select a color');
      return false;
    }

    // For hats, only require front artwork
    if (currentItem.type === 'Hats') {
      if (!formData.artwork.front || formData.artwork.front.length === 0) {
        setError('Please upload your hat embroidery artwork');
        return false;
      }
    } else {
      // For other items, require at least one artwork file
      if (!formData.artwork.front?.length && !formData.artwork.back?.length) {
        setError('Please upload at least one artwork file (front or back)');
        return false;
      }

      // If artwork is uploaded for a side, require its preview to be saved
      if (formData.artwork.front?.length && !snapshots.front) {
        setError('Please save the front preview');
        return false;
      }
      if (formData.artwork.back?.length && !snapshots.back) {
        setError('Please save the back preview');
        return false;
      }
    }

    return true;
  };

  const handleAddItem = () => {
    if (!validateItem()) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      orderItems: [...prev.orderItems, {
        ...currentItem,
        id: Date.now(),
        artwork: prev.artwork,
        notes: prev.notes,
        snapshots
      }],
      artwork: { front: [], back: [] },
      notes: { front: '', back: '' }
    }));

    setCurrentItem({
      type: 'T-Shirts',
      quantity: '',
      size: 'M',
      color: ''
    });

    setSnapshots({ front: null, back: null });
    setError('');
  };

  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.filter(item => item.id !== itemId)
    }));
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-cyan-500 mb-6">
          Order Details
        </h2>
        <p className="text-gray-400 mb-8">
          Configure your custom apparel items and upload your artwork.
        </p>
      </div>

      <OrderItems 
        currentItem={currentItem}
        setCurrentItem={setCurrentItem}
      />

      <DesignArtwork 
        formData={formData}
        currentItem={currentItem}
        onNotesChange={handleNotesChange}
        onArtworkChange={handleArtworkChange}
        onSaveSnapshot={handleSaveSnapshot}
      />

      <div className="border-t border-gray-700 pt-8">
        <div className="flex items-start space-x-4">
          <button
            type="button"
            onClick={handleAddItem}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-black bg-cyan-500 hover:bg-cyan-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Item to Order
          </button>
          
          {error && (
            <div className="flex items-center text-red-500 text-sm">
              <ExclamationCircleIcon className="h-5 w-5 mr-1 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {formData.orderItems.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Order Summary</h4>
            <div className="space-y-2">
              {formData.orderItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center">
                    <div 
                      className="w-6 h-6 rounded-full mr-3 border border-gray-600"
                      style={{ backgroundColor: item.color }}
                    />
                    <div>
                      <p className="text-gray-300">
                        {item.quantity}x {item.size} {productColors.find(c => c.value === item.color)?.name} {item.type}
                      </p>
                      <p className="text-sm text-gray-400">
                        Artwork files: {
                          item.type === 'Hats' 
                            ? item.artwork.front?.length || 0
                            : (item.artwork.front?.length || 0) + (item.artwork.back?.length || 0)
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}